/**
 * Turns Vue's hydration mismatch warnings into something attributable and reportable.
 */

/** Vue prefixes every hydration warning with this; see runtime-core's hydration.ts. */
const HYDRATION_PREFIX = 'Hydration ';

/** What Vue's own console fallback puts in front of the message when no warnHandler is reachable. */
const VUE_WARN_PREFIX = '[Vue warn]: ';

/** Enough of the message to identify the mismatch, without dragging whole DOM dumps into an event. */
const DETAIL_LIMIT = 300;

/** Same reasoning for the DOM path: long enough to name a component, short enough to read. */
const LOCATION_LIMIT = 300;

/** Ancestors to name. Deep enough to escape an atom's own markup, shallow enough to stay legible. */
const MAX_ANCESTORS = 8;

/** Classes per element. BEM here means the first one or two already identify the component. */
const MAX_CLASSES = 3;

export const isHydrationMismatch = (message: string): boolean =>
  message.startsWith(HYDRATION_PREFIX);

export interface HydrationMismatch {
  /** JSS rendering name - the component this mini-app was mounting. */
  jssComponent: string;
  /** Innermost Vue component Vue attributed the mismatch to, or 'unknown' in a production build. */
  vueComponent: string;
  /** e.g. "text mismatch in", "children mismatch on" - the classification Vue used. */
  kind: string;
  detail: string;
  url: string;
  /** DOM path to the drifted node, where Vue could not name a component. */
  location?: string;
}

export interface ReporterOptions {
  jssComponent?: string;
  /** Receives each mismatch that survives dedupe and the cap. Must not throw. */
  report: (mismatch: HydrationMismatch) => void;
  /** Reports allowed per page load. Defaults to 5 - enough to characterise, cheap enough to send. */
  maxReports?: number;
  currentUrl?: () => string;
}

/**
 * `kind` is the part of Vue's message before the location, e.g.
 * "Hydration children mismatch on" -> "children mismatch on". Used for dedupe and for grouping
 * events, so a page that drifts in one place does not look like ten different problems.
 */
const extractKind = (message: string): string =>
  message.slice(HYDRATION_PREFIX.length).split('\n')[0].trim();

/**
 * @returns true when the message was a hydration mismatch and is therefore fully handled -
 *          the caller should not also push it through the generic warning path, which would
 *          duplicate it into Sentry once per node.
 */
export type HydrationMismatchHandler = (
  message: string,
  vueComponent: string,
  trace?: string,
  location?: string,
) => boolean;

export const createHydrationMismatchReporter = ({
  jssComponent = 'unknown',
  report,
  maxReports = 5,
  currentUrl = () => (typeof window === 'undefined' ? '' : window.location.href),
}: ReporterOptions): HydrationMismatchHandler => {
  const seen = new Set<string>();
  let reported = 0;

  return (message, vueComponent, trace = '', location = ''): boolean => {
    if (!isHydrationMismatch(message)) {
      return false;
    }

    const kind = extractKind(message);
    const key = `${jssComponent}|${vueComponent}|${kind}|${location}`;
    if (seen.has(key) || reported >= maxReports) {
      return true; // still handled: swallowed on purpose
    }
    seen.add(key);
    reported += 1;

    report({
      jssComponent,
      vueComponent,
      kind,
      detail: `${message}${trace ? `\n${trace}` : ''}`.slice(0, DETAIL_LIMIT),
      url: currentUrl(),
      ...(location ? { location: location.slice(0, LOCATION_LIMIT) } : {}),
    });
    return true;
  };
};

const isNode = (value: unknown): value is Node =>
  typeof Node !== 'undefined' && value instanceof Node;

/** `div#main.brand-area-content-fi__slide` - a tag plus whatever names it. */
const describeElement = (element: Element): string => {
  const id = element.id ? `#${element.id}` : '';
  const classes = Array.from(element.classList)
    .slice(0, MAX_CLASSES)
    .map((name) => `.${name}`)
    .join('');
  return `${element.tagName.toLowerCase()}${id}${classes}`;
};

/** The drifted node itself, which is often an anonymous comment placeholder from a false `v-if`. */
const describeNode = (node: Node): string => {
  if (node.nodeType === Node.COMMENT_NODE) {
    return `<!--${node.nodeValue ?? ''}-->`;
  }
  if (node.nodeType === Node.TEXT_NODE) {
    return `"${(node.nodeValue ?? '').trim().slice(0, 40)}"`;
  }
  return node.nodeType === Node.ELEMENT_NODE ? describeElement(node as Element) : node.nodeName;
};

const nearestElementSibling = (node: Node, key: 'previousSibling' | 'nextSibling'): string => {
  let current: null | Node = node[key];
  while (current) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      return describeElement(current as Element);
    }
    current = current[key];
  }
  return '';
};

/**
 * Where in the DOM the mismatch happened, as an outermost-first path plus the node's element
 * siblings. This has to be read synchronously while the warning is being logged: Vue removes the
 * server node immediately afterwards (`remove(node)` in handleMismatch), so a node clicked in the
 * console later is detached and reveals nothing in the Elements panel.
 *
 * The path is a stand-in for the component name, not a replacement: with BEM class names it usually
 * pins the component and the exact element within it.
 */
export const describeNodeLocation = (node: unknown): string => {
  if (!isNode(node)) {
    return '';
  }

  const path = [describeNode(node)];
  let ancestor = node.parentElement;
  // <body> and <html> are the same on every page, so they would only push the useful part out.
  while (ancestor && ancestor !== document.body && path.length <= MAX_ANCESTORS) {
    path.unshift(describeElement(ancestor));
    ancestor = ancestor.parentElement;
  }

  const previous = nearestElementSibling(node, 'previousSibling');
  const next = nearestElementSibling(node, 'nextSibling');
  const siblings = [previous && `after ${previous}`, next && `before ${next}`].filter(Boolean);

  return `${path.join(' > ')}${siblings.length ? ` [${siblings.join(', ')}]` : ''}`;
};

const formatWarnArg = (arg: unknown): string => {
  if (isNode(arg)) {
    return describeNode(arg);
  }
  return typeof arg === 'string' ? arg : String(arg);
};

/**
 * Catches hydration mismatches in a production build, where app.config.warnHandler is unreachable:
 * Vue's `warn` takes its component context from a stack that only dev builds push to (every
 * pushWarningContext call is NODE_ENV-gated), so with no instance it falls through to console.warn
 * with the offending node among the arguments. That fallback is the only signal left, and this turns
 * it back into a report.
 *
 * `getHandler` is read per warning rather than captured, so the caller can point it at whichever
 * mini-app is currently mounting and keep the JSS rendering name in the report.
 *
 * @returns a function that restores the original console.warn.
 */
export const installHydrationWarnInterceptor = (
  getHandler: () => HydrationMismatchHandler | null | undefined,
): (() => void) => {
  /* eslint-disable no-console -- replacing console.warn is this function's entire purpose */
  const original = console.warn;

  const intercept = (...args: unknown[]) => {
    try {
      const [first, ...rest] = args;
      const message =
        typeof first === 'string' && first.startsWith(VUE_WARN_PREFIX)
          ? first.slice(VUE_WARN_PREFIX.length)
          : '';
      if (isHydrationMismatch(message)) {
        getHandler()?.(
          message,
          'unknown',
          rest.map(formatWarnArg).join(' '),
          describeNodeLocation(rest.find(isNode)),
        );
      }
    } catch {
      // a diagnostic must never be the reason console.warn throws
    }
    original.apply(console, args);
  };

  console.warn = intercept;

  const restore = () => {
    if (console.warn === intercept) {
      console.warn = original;
    }
  };
  /* eslint-enable no-console */

  return restore;
};
